// frontend/src/pages/McpToolIndex.jsx
import React, { useEffect, useRef, useState } from "react";
import api from "../api";

// Activity inside the iframe does not reach the parent window, so App's idle
// timer would log the user out while they work in this tab. Re-dispatch those
// events on the parent window. Same list App.js listens for.
const ACTIVITY_EVENTS = ["click", "keydown", "mousemove", "scroll", "touchstart"];

export default function McpToolIndex() {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const frameRef = useRef(null);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        // The page is served by the API behind the session cookie (admin/manager only)
        const r = await api.get("/mcp-tool-index", { responseType: "text" });
        // A srcdoc document inherits this app's URL as its base, so the page's
        // in-page links (the "Skip to results" href="#main") would resolve to
        // roc.../#main and load the whole app inside the iframe. Pin the base to
        // the iframe's own document so they stay in-page jumps.
        const page = (r.data || "").replace(/<head>/i, '<head><base href="about:srcdoc">');
        if (!ignore) setHtml(page);
      } catch (err) {
        if (!ignore) {
          const status = err?.response?.status;
          setError(
            status === 403
              ? "You need the admin or manager role to view the MCP Tool Index."
              : `Failed to load the MCP Tool Index${err?.message ? ` — ${err.message}` : ""}`
          );
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => { ignore = true; };
  }, []);

  const handleFrameLoad = () => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    const forward = (e) => window.dispatchEvent(new Event(e.type));
    ACTIVITY_EVENTS.forEach((evt) => win.addEventListener(evt, forward, { passive: true }));
  };

  if (loading) return <div className="text-muted">Loading…</div>;

  if (error) {
    return <div className="alert alert-danger py-2" role="alert">{error}</div>;
  }

  return (
    <iframe
      ref={frameRef}
      title="MCP Tool Index"
      srcDoc={html}
      onLoad={handleFrameLoad}
      style={{
        width: "100%",
        height: "calc(100vh - 160px)",
        minHeight: "480px",
        border: "1px solid var(--bs-border-color, #dee2e6)",
        borderRadius: "6px",
        display: "block",
      }}
    />
  );
}
